import { reconcileReactionControls } from './08-reactions.mjs';

// Owns ordered snapshot application and narrowly scoped post mutation presentation.
const invalidatesFeed = new Set([
  'block',
  'friend',
  'friend-request',
  'follow',
  'close-friend',
  'connection-label',
  'social-privacy',
  'hide',
  'delete',
  'publish',
]);
const localPostChanges = new Set([
  'like',
  'save',
  'comment',
  'vote',
  'post-reaction',
  'comment-reaction',
]);

/** Serialize authoritative mutations so slow responses cannot overwrite newer snapshots.
 * Account changes invalidate queued work and responses; a failed command does not poison the queue.
 */
export function createCommandQueue({ actor, send, apply }) {
  let tail = Promise.resolve();
  return (input, options = {}) => {
    const owner = actor();
    const operation = tail.then(async () => {
      if (!owner || actor() !== owner) throw new Error('Sign in to continue.');
      const snapshot = await send(input, options);
      if (actor() !== owner) throw new Error('Your session changed.');
      await apply(snapshot, input, options);
      return snapshot;
    });
    tail = operation.catch(() => {});
    return operation;
  };
}

const experienceOnly = (input) =>
  input.type === 'settings' &&
  Object.keys(input).length === 2 &&
  typeof input.experience === 'string';

/** Reconcile post actions in place; intentional navigation and eligibility changes rebuild views. */
export function createMutations(ctx) {
  return createCommandQueue({
    actor: () => ctx.state?.me.id,
    send: (input, options) =>
      experienceOnly(input) && !options.fullSnapshot
        ? ctx.api('/api/feed/preference', { experience: input.experience })
        : ctx.api('/api/command', input),
    apply: async (snapshot, input, { paint = true }) => {
      if (experienceOnly(input) && !snapshot.me) {
        ctx.state.settings = { ...ctx.state.settings, experience: snapshot.experience };
        if (paint) ctx.render();
        return;
      }
      ctx.state = snapshot;
      reconcileReactionControls(ctx);
      if (snapshot.settings.locale !== ctx.currentLocale())
        await ctx.setLocale(snapshot.settings.locale);
      if (invalidatesFeed.has(input.type)) {
        ctx.feed.generation += 1;
        ctx.feed.cached = null;
        ctx.feed.pending = null;
      }
      if (!paint) return;
      if (localPostChanges.has(input.type)) {
        const post = snapshot.posts.find((p) => p.id === input.id);
        for (const card of ctx.view.querySelectorAll('.post')) {
          if (card.dataset.postId !== input.id) continue;
          if (!post) card.remove();
          else
            ctx.updatePostCard(card, post, {
              reason: ctx.feed.items.find((p) => p.id === input.id)?.reason,
              expanded: ctx.route()[0] === 'post',
            });
        }
        return;
      }
      ctx.render();
    },
  });
}
